import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useMemo } from 'react'
import { operations } from 'store/courseManager'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import { ERROR_TYPES } from 'layers/content/constants'
import { elementary, middleSchool } from 'utils/product/configs'
import {
  GRADE_K2_TIERED_INTERVENTIONS,
  GRADE_35_TIERED_INTERVENTIONS,
  GRADE_68_TIERED_INTERVENTIONS,
} from 'utils/program/types'
import { HIGH_SCHOOL } from './constants'

export const CourseNames = {
  GradeK: 'K',
  Grade1: '1',
  Grade2: '2',
  Grade3: '3',
  Grade4: '4',
  Grade5: '5',
  Grade6: '6',
  Grade7: '7',
  Grade8: '8',
  // course name is all lower-case: 'highschool'
  GradeHighSchool: HIGH_SCHOOL.toLowerCase(),
  // Training courses
  ElementaryTraining: 'k-5_training_tier1',
  MiddleSchoolTraining: '6-8_training_tier1',
  GradeK2TieredInterventions: GRADE_K2_TIERED_INTERVENTIONS,
  Grade35TieredInterventions: GRADE_35_TIERED_INTERVENTIONS,
  Grade68TieredInterventions: GRADE_68_TIERED_INTERVENTIONS,
}

// Helper function to get the program key from a program name
/**
 * Maps a program name to its corresponding program key used in the user context.
 *
 * @param {string} programName - The name of the program (e.g., 'elementary', 'middle-school', 'highSchool')
 * @returns {string|null} The corresponding program key for accessing user context data, or null if not found
 *
 * @example
 * getProgramKey('elementary') // returns 'k5SelDigital'
 * getProgramKey('middle-school') // returns 'mspDigital'
 * getProgramKey('highSchool') // returns 'highSchool'
 * getProgramKey('unknown') // returns null
 */
export function getProgramKey(programName) {
  switch (programName) {
    case 'elementary':
    case 'elementaryWithTraining':
    case 'elementaryTrainingOnly':
    case 'elementaryTieredInterventions':
      return 'k5SelDigital'

    case 'middle-school':
    case 'middleSchool':
    case 'middleSchoolWithTraining':
    case 'middleSchoolTrainingOnly':
    case 'middleSchoolTieredInterventions':
      return 'mspDigital'
    case 'highSchool':
      return 'highSchool'
    default:
      return null
  }
}

export const programs = {
  elementary: elementary.productName,
  middleSchool: middleSchool.productName,
  highSchool: HIGH_SCHOOL,
  // Programs with training courses
  elementaryWithTraining: 'elementaryWithTraining',
  middleSchoolWithTraining: 'middleSchoolWithTraining',
  // Trainings only
  elementaryTrainingOnly: 'elementaryTrainingOnly',
  middleSchoolTrainingOnly: 'middleSchoolTrainingOnly',
  elementaryTieredInterventions: 'elementaryTieredInterventions',
  middleSchoolTieredInterventions: 'middleSchoolTieredInterventions',
  // special case to make the higher order component a non-operational component:
  none: 'none',
}

function findCourse(courses, courseName) {
  return courses?.find(course => course?.name === courseName)
}

function resolveContext(programKey) {
  return (userContext, courseName) => {
    const clone = structuredClone(userContext)
    if (!userContext?.sites) {
      console.warn('userContext.sites is undefined')
      return clone
    }
    const sites = clone.sites
      ?.filter(site => {
        return !!site?.programs?.[programKey]
      })
      ?.map(site => {
        const program = site.programs[programKey]
        const existingCourse = findCourse(program?.courses || [], courseName)
        const hasValidExistingCourse =
          existingCourse &&
          typeof existingCourse.name === 'string' &&
          existingCourse.name.trim().length > 0

        // Prefer a valid course from context; otherwise fall back to placeholder
        program.courses = hasValidExistingCourse
          ? [
              {
                ...existingCourse,
                // Ensure we request latest version from Course Manager
                version: null,
              },
            ]
          : [
              {
                name: courseName,
                // Request latest version when context is missing/invalid
                version: null,
              },
            ]

        return site
      })

    const preparedContext = {
      ...clone,
      sites,
    }

    return preparedContext
  }
}

export const ProgramToCoursesMap = {
  [elementary.productName]: {
    courses: [
      CourseNames.GradeK,
      CourseNames.Grade1,
      CourseNames.Grade2,
      CourseNames.Grade3,
      CourseNames.Grade4,
      CourseNames.Grade5,
    ],
    contextResolver: resolveContext('k5SelDigital'),
  },
  [middleSchool.productName]: {
    courses: [CourseNames.Grade6, CourseNames.Grade7, CourseNames.Grade8],
    contextResolver: resolveContext('mspDigital'),
  },
  [HIGH_SCHOOL]: {
    courses: [CourseNames.GradeHighSchool],
    contextResolver: resolveContext('highSchool'),
  },
  // Programs with training courses
  [programs.elementaryWithTraining]: {
    courses: [
      CourseNames.GradeK,
      CourseNames.Grade1,
      CourseNames.Grade2,
      CourseNames.Grade3,
      CourseNames.Grade4,
      CourseNames.Grade5,
      CourseNames.ElementaryTraining,
    ],
    contextResolver: resolveContext('k5SelDigital'),
  },
  [programs.middleSchoolWithTraining]: {
    courses: [
      CourseNames.Grade6,
      CourseNames.Grade7,
      CourseNames.Grade8,
      CourseNames.MiddleSchoolTraining,
    ],
    contextResolver: resolveContext('mspDigital'),
  },
  [programs.elementaryTrainingOnly]: {
    courses: [CourseNames.ElementaryTraining],
    contextResolver: resolveContext('k5SelDigital'),
  },
  [programs.middleSchoolTrainingOnly]: {
    courses: [CourseNames.MiddleSchoolTraining],
    contextResolver: resolveContext('mspDigital'),
  },
  [programs.elementaryTieredInterventions]: {
    courses: [
      CourseNames.GradeK2TieredInterventions,
      CourseNames.Grade35TieredInterventions,
    ],
    contextResolver: resolveContext('k5SelDigital'),
  },
  [programs.middleSchoolTieredInterventions]: {
    courses: [CourseNames.Grade68TieredInterventions],
    contextResolver: resolveContext('mspDigital'),
  },
}

export function useCourseManager(options) {
  const { program, overrideHsPreferences } = options || {}

  const dispatch = useDispatch()
  const userContext = useUserContext()

  // Use a stable reference for overrideHsPreferences
  const stableOverrideHsPreferences = useRef(overrideHsPreferences)
  const stableProgram = useRef(program)

  // Update refs only when the values actually change (deep comparison)
  useEffect(() => {
    if (
      JSON.stringify(stableOverrideHsPreferences.current) !==
      JSON.stringify(overrideHsPreferences)
    ) {
      stableOverrideHsPreferences.current = overrideHsPreferences
        ? { ...overrideHsPreferences }
        : null
    }

    if (stableProgram.current !== program) {
      stableProgram.current = program
    }
  }, [program, overrideHsPreferences])

  // Create a stable reference for the course manager state
  const { loading, storedCourses, error, isUserContextLoading } = useSelector(
    state => {
      const storedCourses = state?.courseManager?.courses ?? {}
      const error = state?.courseManager?.error
      const loading = state?.courseManager?.isFetching

      const isUserContextLoading = state?.userContextManager?.isFetching

      return {
        storedCourses,
        error: error
          ? { message: error, errorType: ERROR_TYPES.courseManagerError }
          : null,
        loading,
        isUserContextLoading,
      }
    },
  )

  // Combine both checks to ensure we have a valid user context
  const isUserContextValid =
    !isUserContextLoading &&
    userContext &&
    userContext?.sites &&
    userContext?.sites?.length > 0

  // Memoize the courses and contextResolver to prevent unnecessary re-renders
  const { courses, contextResolver } = useMemo(() => {
    const programConfig = ProgramToCoursesMap?.[stableProgram.current] || {
      courses: [],
      contextResolver: () => ({}),
    }

    return {
      courses: programConfig.courses || [],
      contextResolver: programConfig.contextResolver,
    }
  }, [stableProgram.current])

  /* https://secondstep.atlassian.net/browse/LEARN-18665
    Temporary workaround to allow "mark complete" learn experiences
    outside the users course tree
  */
  // Memoize the overridden user context to prevent unnecessary re-renders
  const overriddenUserContext = useMemo(() => {
    if (!isUserContextValid) return null

    const clonedContext = structuredClone(userContext)

    // Only apply overrides if userContext is loaded and overrides exist
    if (
      stableOverrideHsPreferences.current &&
      Object.keys(stableOverrideHsPreferences.current).length > 0
    ) {
      const activeSiteIndex = userContext?.sites?.findIndex(
        s => s?.siteId === userContext?.hsActiveSiteId,
      )

      if (activeSiteIndex > -1) {
        const site = clonedContext?.sites?.[activeSiteIndex]
        if (site && site?.programs?.highSchool) {
          site.programs.highSchool.preferences = {
            ...site?.programs?.highSchool?.preferences,
            ...stableOverrideHsPreferences.current,
          }
        }
      }
    }

    return clonedContext
  }, [isUserContextValid, userContext, stableOverrideHsPreferences.current])

  // Use a ref to track if we've already fetched for this program and preferences
  // This persists across re-renders and component remounts
  const fetchTracker = useRef({
    hasFetched: false,
    program: null,
    overrideHsPreferences: null,
    fetchedCourses: new Set(), // Track which courses we've already fetched
  })

  // Global cache for course trees to persist across component remounts
  // This is a static variable shared across all instances of the hook
  if (!window.__courseManagerCache) {
    window.__courseManagerCache = {
      programCache: {},
      courseCache: new Set(),
    }
  }

  useEffect(() => {
    // Only make requests if userContext is fully loaded
    if (!isUserContextValid) {
      return // Exit early if userContext is not ready
    }

    const currentProgram = stableProgram.current
    const currentPreferences = stableOverrideHsPreferences.current

    // Check if we need to fetch based on program or preferences changes
    const programChanged = fetchTracker.current.program !== currentProgram
    const preferencesChanged =
      JSON.stringify(fetchTracker.current.overrideHsPreferences) !==
      JSON.stringify(currentPreferences)

    // Check global cache first
    const programCacheKey = `${currentProgram}-${JSON.stringify(
      currentPreferences,
    )}`
    const isProgramCached =
      window.__courseManagerCache.programCache[programCacheKey]

    // Only fetch if we haven't fetched this program+preferences combination before
    // or if the program or preferences have changed
    if (isProgramCached && !programChanged && !preferencesChanged) {
      // Program is already cached, no need to fetch
      return
    }

    // Additional validation to prevent requests with invalid user context
    // Check if the user has access to the current program
    const hasProgramAccess = userContext?.sites?.some(site => {
      const programKey = getProgramKey(currentProgram)
      return !!site?.programs?.[programKey]
    })

    if (!hasProgramAccess) {
      console.warn(
        `[useCourseManager] User does not have access to program: ${currentProgram}. Skipping API requests.`,
      )
      return // Skip API requests if user doesn't have access to this program
    }

    // Update the global cache
    window.__courseManagerCache.programCache[programCacheKey] = true

    // Update the tracker with current values
    fetchTracker.current = {
      hasFetched: true,
      program: currentProgram,
      overrideHsPreferences: currentPreferences
        ? { ...currentPreferences }
        : null,
      fetchedCourses: fetchTracker.current.fetchedCourses,
    }

    // Now fetch the course trees, but only for courses we haven't fetched yet
    for (const course of courses) {
      const courseCacheKey = `${currentProgram}-${course}-${JSON.stringify(
        currentPreferences,
      )}`

      // Skip if we've already fetched this course for this program+preferences
      if (window.__courseManagerCache.courseCache.has(courseCacheKey)) {
        continue
      }

      // Prepare the user context for this specific course
      const preparedUserContext = contextResolver(overriddenUserContext, course)

      // Additional validation to ensure the prepared context has valid sites and programs
      const hasValidSites =
        preparedUserContext?.sites &&
        Array.isArray(preparedUserContext.sites) &&
        preparedUserContext.sites.length > 0

      // Skip this course if the prepared context is invalid
      if (!hasValidSites) {
        console.warn(
          `[useCourseManager] Invalid user context for course: ${course}. Skipping API request.`,
        )
        continue
      }

      // Validate that at least one site has the program and course
      const hasValidCourse = preparedUserContext.sites.some(site => {
        const programKey = getProgramKey(currentProgram)
        return site?.programs?.[programKey]?.courses?.some(c => !!c)
      })

      if (!hasValidCourse) {
        console.warn(
          `[useCourseManager] No valid courses found for program: ${currentProgram}, course: ${course}. Skipping API request.`,
        )
        continue
      }

      // Mark this course as fetched in the global cache
      window.__courseManagerCache.courseCache.add(courseCacheKey)

      // Dispatch the fetch action only if we have valid data
      dispatch(
        operations.fetchCourseTree({
          userContext: preparedUserContext,
          courseName: course,
        }),
      )
    }
  }, [
    // Only include dependencies that should trigger a re-evaluation
    isUserContextValid,
    dispatch,
    courses,
    contextResolver,
    overriddenUserContext,
    userContext?.sites,
  ])

  // Memoize the course trees to prevent unnecessary re-renders
  const courseTrees = useMemo(() => {
    const trees = {}
    for (const course of courses) {
      if (storedCourses?.[course]) {
        trees[course] = Object.freeze(storedCourses?.[course])
      }
    }
    return trees
  }, [courses, storedCourses])

  // Clean up the cache when the component unmounts
  useEffect(() => {
    return () => {
      // We don't clean up the global cache on unmount because we want to persist it
      // across component remounts. This is intentional to prevent repeated fetches.
    }
  }, [])

  return {
    courseTrees,
    loading,
    error,
    isUserContextLoading, // Expose the userContext loading state
  }
}
